import { useEffect, useState } from 'react'

import { useLazyQuery } from '@apollo/client';

import ENV from "../app/constants/env";
import getObjectCommand from '../app/constants/utils';
import { FETCH_ANNOUNCEMENT_CONFIG } from '../app/graphql/queries';
import { userIdSelector } from '../app/main/mainSlice';
import { useAppSelector } from '../app/store/hooks';
import { isJsonString } from '../components/newHomePage/utils/newHomePageUtils';
import { logEvents } from '../utils/logging';

const {
  HOMEPAGE_BUCKET_NAME,
  ANNOUNCEMENT_KEY,
} = ENV;
interface AnnouncementData {
  image?: string | null,
  type?: string | null,
  manual?: {
    background_color?: string | null,
    title?: string | null,
    subtitle?: string | null,
    image?: string | null
  } | null,
  button_text?: string | null,
  action?: string | null
}

interface LocalStorageData {
  localAnnouncementData?: AnnouncementData;
  localAnnouncementVersion?: number;
  globalAnnouncementData?: AnnouncementData;
  globalAnnouncementVersion?: number;
  popupviewedLocal?: boolean;
  popupviewedGlobal?: boolean;
}

const updateLocalStorage = (
  localStorageData: LocalStorageData,
  data: AnnouncementData,
  version: number,
  type: 'local' | 'global'
) => {
  const updatedData: LocalStorageData = {
    ...localStorageData,
    [`${type}AnnouncementData`]: data,
    [`${type}AnnouncementVersion`]: version,
    [`popupviewed${type.charAt(0).toUpperCase() + type.slice(1)}`]: false
  };
  localStorage.setItem('announcement_data', JSON.stringify(updatedData));
};

const checkAndSetPopup = (
  setPopupData: (data: AnnouncementData) => void,
  setShowPopUp: (show: boolean) => void,
  setActivePopupType: (popupType: 'local' | 'global') => void,
  userId: string | undefined

) => {
  const localStorageData = isJsonString(localStorage.getItem('announcement_data')) ? JSON.parse(localStorage.getItem('announcement_data') || '{}') : {};

  const { localAnnouncementData, popupviewedLocal, globalAnnouncementData, popupviewedGlobal, localAnnouncementVersion, globalAnnouncementVersion } = localStorageData;

  if (!popupviewedLocal && localAnnouncementData && Object.keys(localAnnouncementData).length > 0) {
    setPopupData(localAnnouncementData);
    setShowPopUp(true);
    setActivePopupType('local');

    const payload = {
      eventData: {
        type: 'local',
        announcement_data: localAnnouncementData,
        announcement_version: localAnnouncementVersion,
        userId
      },
    };
    logEvents('null', 'APP_POPUPBANNER_SHOWN', payload)

  } else if (!popupviewedGlobal && globalAnnouncementData && Object.keys(globalAnnouncementData).length > 0) {
    setPopupData(globalAnnouncementData);
    setShowPopUp(true);
    setActivePopupType('global');

    const payload = {
      eventData: {
        type: 'global',
        announcement_data: globalAnnouncementData,
        announcement_version: globalAnnouncementVersion,
        userId
      },
    };

    logEvents('null', 'APP_POPUPBANNER_SHOWN', payload)

  } else {
    setShowPopUp(false);

  }
};

const useGetAnnouncement = (loadLazy?: string) => {
  const [showPopup, setShowPopUp] = useState<boolean>(false);
  const [popupData, setPopupData] = useState<AnnouncementData>({
    image: '',
    type: '',
    manual: null,
    button_text: "",
    action: ''
  });
  const [activePopupType, setActivePopupType] = useState<'local' | 'global' | null>(null);

  const userId = useAppSelector(userIdSelector);
  const localStorageData = JSON.parse(localStorage.getItem('announcement_data') || '{}');
  const { localAnnouncementVersion, globalAnnouncementVersion } = localStorageData;
  const [fetchAnnouncementData] = useLazyQuery(FETCH_ANNOUNCEMENT_CONFIG);

  const fetchAnnouncement = async () => {
    const dbAnnouncementData = await fetchAnnouncementData({
      variables: { userId },
    });
    const fetchedAnnouncementVersion = dbAnnouncementData?.data?.User_Home_Config?.[0]?.mobileAnnouncementVersion;
    const fetchedAnnouncementData = dbAnnouncementData?.data?.User_Home_Config?.[0]?.mobileAnnouncementConfig;

    if (fetchedAnnouncementData && Object.keys(fetchedAnnouncementData).length > 0 && fetchedAnnouncementVersion !== localAnnouncementVersion) {
      //  new local version issued
      updateLocalStorage(localStorageData, fetchedAnnouncementData, fetchedAnnouncementVersion, 'local');
    } else if (!fetchedAnnouncementData || fetchedAnnouncementVersion === localAnnouncementVersion) {
      const key = ANNOUNCEMENT_KEY;
      const fetchedGlobalAnnouncementData = await getObjectCommand(HOMEPAGE_BUCKET_NAME, key);
      const sanitizedGlobalAnnouncementData = isJsonString(fetchedGlobalAnnouncementData) ? JSON.parse(fetchedGlobalAnnouncementData) : JSON.parse(JSON.stringify(fetchedGlobalAnnouncementData));

      if (sanitizedGlobalAnnouncementData && Object.keys(sanitizedGlobalAnnouncementData).length > 0 && globalAnnouncementVersion !== sanitizedGlobalAnnouncementData.announcement_version) {
        //  new global version issued
        updateLocalStorage(localStorageData, sanitizedGlobalAnnouncementData.announcement_config, sanitizedGlobalAnnouncementData.announcement_version, 'global');
      }
    }

    checkAndSetPopup(setPopupData, setShowPopUp, setActivePopupType, userId);
  };

  useEffect(() => {
    if (!loadLazy) {
      fetchAnnouncement()
    }
  }, [loadLazy])

  const onClose = (type?: string) => {
    const payload = {
      eventData: {
        type: activePopupType,
        announcement_data: activePopupType === 'local' ? localStorageData.localAnnouncementData : localStorageData.globalAnnouncementData,
        announcement_version: activePopupType === 'local' ? localStorageData.localAnnouncementVersion : localStorageData.globalAnnouncementVersion,
        userId
      },
    };

    if (type === "CTA_CLICKED") {
      logEvents('null', 'APP_POPUPBANNER_CLICKED', payload)
    }
    else {
      logEvents('null', 'APP_POPUPBANNER_CLOSED', payload)
    }

    if (activePopupType === 'local') {
      localStorage.setItem('announcement_data', JSON.stringify({
        ...localStorageData,
        popupviewedLocal: true
      }));
    } else if (activePopupType === 'global') {
      localStorage.setItem('announcement_data', JSON.stringify({
        ...localStorageData,
        popupviewedGlobal: true
      }));
    }

    setActivePopupType(null);
    setShowPopUp(false);

  };

  return { showPopup, onClose, popupData }
}

export default useGetAnnouncement
